import React, { useLayoutEffect, useRef, useState } from "react";
import styles from "./FlipCard.module.scss";
import cn from "classnames";
import useResizeObserver from "use-resize-observer";
import { twMerge } from "tailwind-merge";
import usePrevious from "../../hooks/usePrevious";
import { useAnnounce } from "../../hooks/useAnnounce";
const FlipCard = (props) => {
    const frontRef = useRef(null);
    const backRef = useRef(null);
    const { height: frontHeight } = useResizeObserver({ ref: frontRef });
    const { height: backHeight } = useResizeObserver({ ref: backRef });
    const frontSideHeight = frontHeight;
    let backSideHeight = backHeight;
    const bothSidesHaveHeight = !!frontSideHeight && !!backSideHeight;
    if (bothSidesHaveHeight && backSideHeight < frontSideHeight) {
        backSideHeight = frontSideHeight;
    }
    const backSideTransform = !bothSidesHaveHeight
        ? "rotateY(180deg) translateY(0)"
        : !props.isFlipped
            ? `rotateY(180deg) scaleY(${frontSideHeight / backSideHeight})`
            : undefined;
    const frontSideTransform = bothSidesHaveHeight && props.isFlipped
        ? `scaleY(${backSideHeight / frontSideHeight})`
        : undefined;
    const containerHeight = !bothSidesHaveHeight
        ? undefined
        : props.isFlipped
            ? backSideHeight
            : frontSideHeight;
    const previousIsFlipped = usePrevious(props.isFlipped);
    const [isAnimating, setIsAnimating] = useState(false);
    const announce = useAnnounce();
    const ref = useRef(null);
    useLayoutEffect(() => {
        if (previousIsFlipped != null && props.isFlipped != previousIsFlipped) {
            setIsAnimating(true);
            ref.current?.focus();
            announce(`You've flipped a card. ${props.isFlipped ? "Back" : "Front"} side is now visible`);
        }
    }, [props.isFlipped, previousIsFlipped]);
    // ensures that height is animatable only when it's triggered by flip
    // it solves the issue with nested height changes
    const isAnimationAboutToStart = props.isFlipped != previousIsFlipped;
    let height;
    let maxHeight;
    if (isAnimating) {
        height = containerHeight;
        maxHeight = undefined;
    }
    else if (isAnimationAboutToStart) {
        height = bothSidesHaveHeight // previous side height
            ? props.isFlipped
                ? frontSideHeight
                : backSideHeight
            : undefined;
        maxHeight = undefined;
    }
    else {
        height = undefined;
        maxHeight = containerHeight;
    }
    return (React.createElement("div", { className: styles.flipCard },
        React.createElement("div", { tabIndex: -1, ref: ref, className: "sr-only" }),
        React.createElement("div", { style: {
                height: height,
                maxHeight: maxHeight,
                pointerEvents: props.isFlipped ? "none" : "auto",
            }, className: cn(styles.flipCardInner, props.isFlipped && styles.flipped) },
            React.createElement("div", { ref: frontRef, className: twMerge(styles.flipCardFront, "rounded-[10px]", props.frontAndBackClassName), onTransitionEnd: (event) => {
                    if (event.propertyName === "transform") {
                        setIsAnimating(false);
                    }
                }, style: {
                    transform: frontSideTransform,
                    visibility: props.isFlipped && !isAnimating ? "hidden" : undefined,
                } }, props.front),
            React.createElement("div", { ref: backRef, onTransitionEnd: (event) => {
                    if (event.propertyName === "transform") {
                        setIsAnimating(false);
                    }
                }, className: twMerge(styles.flipCardBack, "rounded-[10px]", props.frontAndBackClassName), style: {
                    zIndex: -1,
                    pointerEvents: props.isFlipped ? "auto" : "none",
                    position: bothSidesHaveHeight ? undefined : "unset",
                    transform: backSideTransform,
                    visibility: !props.isFlipped && !isAnimating ? "hidden" : undefined,
                } }, props.renderBack({
                minHeight: frontSideHeight,
                boxSizing: "border-box",
            })))));
};
export default FlipCard;
//# sourceMappingURL=FlipCard.js.map